export const TECHNO_BPM = 128;

const SIXTEENTH = 60 / TECHNO_BPM / 4;
const BAR = SIXTEENTH * 16;
const LOOKAHEAD_S = 0.1;
const TICK_MS = 25;

// Am, F, C, G: the vi-IV-I-V loop half of trance is written on. Chords are close voicings
// around middle C; roots drive the bass.
const CHORDS = [
  [220, 261.63, 329.63],
  [174.61, 220, 261.63],
  [196, 261.63, 329.63],
  [196, 246.94, 293.66]
];
const ROOTS = [110, 87.31, 130.81, 98];

// Pluck arp: sixteenths over the chord across two octaves (indices 3-5 are the octave up).
// One pattern for every chord; a per-chord variant was tried and made the drop too busy.
const ARP_PATTERN = [0, 2, 4, 3, 5, 4, 3, 1, 0, 2, 4, 3, 5, 3, 4, 2];

// Trance gate on the chord stabs: 1 = open for that sixteenth.
const GATE = [1, 0, 1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 1];

// Rolling bass: the three sixteenths between kicks.
const BASS_STEPS = new Set([1, 2, 3, 5, 6, 7, 9, 10, 11, 13, 14, 15]);

// A minor, the notes the melody uses.
const N = {
  G4: 392,
  A4: 440,
  B4: 493.88,
  C5: 523.25,
  D5: 587.33,
  E5: 659.25,
  F5: 698.46,
  G5: 783.99,
  A5: 880
};

/** [step in bar, frequency, length in sixteenths]. */
type Note = [number, number, number];

/**
 * The hook is one rhythm, repeated: dotted eighth, dotted eighth, eighth (the trance 3+3+2),
 * then two quarters. Every bar keeps that rhythm and only the pitches move with the chord,
 * which is what makes a hook stick. An earlier version varied the rhythm every bar and was
 * far too busy.
 */
const motif = (
  a: number,
  b: number,
  c: number,
  d: number,
  e: number
): Note[] => [
  [0, a, 3],
  [3, b, 3],
  [6, c, 2],
  [8, d, 4],
  [12, e, 4]
];

/** The answer: same 3+3+2 opening, then one held half note instead of two quarters. */
const answer = (a: number, b: number, c: number, d: number): Note[] => [
  [0, a, 3],
  [3, b, 3],
  [6, c, 2],
  [8, d, 8]
];

/**
 * The drop hook: four bars of call, four of answer. The only sixteenths are the four at the
 * very end, stepping A B C D straight back into the E that opens bar 1.
 */
const DROP_HOOK: Note[][] = [
  motif(N.E5, N.E5, N.D5, N.C5, N.E5),
  motif(N.F5, N.F5, N.E5, N.C5, N.A4),
  motif(N.G5, N.G5, N.E5, N.C5, N.E5),
  motif(N.D5, N.D5, N.B4, N.G4, N.B4),
  answer(N.E5, N.E5, N.D5, N.A5),
  answer(N.A5, N.A5, N.G5, N.F5),
  answer(N.G5, N.G5, N.E5, N.C5),
  [
    [0, N.D5, 3],
    [3, N.D5, 3],
    [6, N.B4, 2],
    [8, N.D5, 4],
    [12, N.A4, 1],
    [13, N.B4, 1],
    [14, N.C5, 1],
    [15, N.D5, 1]
  ]
];

/**
 * The same tune in the breakdown, slowed to its outline: one held note and two quarters per
 * bar, following the first note of each drop bar, so the drop lands as the full-speed payoff
 * of something you have already heard.
 */
const BREAKDOWN_THEME: Note[][] = [
  [
    [0, N.E5, 8],
    [8, N.D5, 4],
    [12, N.C5, 4]
  ],
  [
    [0, N.F5, 8],
    [8, N.E5, 4],
    [12, N.C5, 4]
  ],
  [
    [0, N.G5, 8],
    [8, N.E5, 4],
    [12, N.C5, 4]
  ],
  [
    [0, N.D5, 8],
    [8, N.B4, 4],
    [12, N.G4, 4]
  ]
];

type Section = "intro" | "build" | "breakdown" | "drop";

// Four bars of intro, then build (4), breakdown (4) and drop (8, the whole hook), on repeat.
const CYCLE_BARS = 16;
const sectionAt = (bar: number): Section => {
  if (bar < 4) return "intro";
  const b = (bar - 4) % CYCLE_BARS;
  if (b < 4) return "build";
  if (b < 8) return "breakdown";
  return "drop";
};

const PAD_CUTOFF: Record<Section, number> = {
  intro: 500,
  build: 1300,
  breakdown: 3200,
  drop: 2400
};

// Seven detuned voices spread across the stereo field: the supersaw.
const SUPERSAW = [
  { cents: -18, pan: -0.8 },
  { cents: -11, pan: -0.5 },
  { cents: -5, pan: -0.2 },
  { cents: 0, pan: 0 },
  { cents: 5, pan: 0.2 },
  { cents: 11, pan: 0.5 },
  { cents: 18, pan: 0.8 }
];

// Soft-clip curve that gives the kick its punch.
const DRIVE_CURVE = Float32Array.from({ length: 256 }, (_, i) =>
  Math.tanh((i / 128 - 1) * 2.5)
);

export type TechnoBeat = {
  start: () => void;
  stop: () => void;
  /**
   * When the first downbeat is heard, in document-timeline milliseconds (the clock
   * performance.now() and CSS animations use), or null while silent. Lets the visuals lock to
   * the kick.
   */
  downbeatMs: () => number | null;
};

/**
 * A small trance/techno track synthesized with Web Audio, so there are no audio assets.
 *
 * What makes it sound like the genre rather than a chiptune: supersaws (seven detuned saws per
 * note, spread in stereo) for the pad, gated chords, pluck arp and lead; a sidechain pump that
 * ducks everything melodic on every kick; a long generated reverb and a ping-pong echo; a
 * rolling sixteenth bass between the kicks; and an arrangement with a breakdown, a noise
 * riser and a drop.
 *
 * Steps are scheduled slightly ahead on the audio clock (the usual lookahead pattern), so the
 * timing holds while the main thread renders the grid. start() must run inside a user gesture,
 * or the browser keeps the AudioContext suspended.
 */
export const createTechnoBeat = (): TechnoBeat => {
  let ctx: AudioContext | null = null;
  let drums: GainNode | null = null;
  let music: GainNode | null = null;
  let reverbSend: GainNode | null = null;
  let echoSend: GainNode | null = null;
  let noise: AudioBuffer | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let nextTime = 0;
  let firstDownbeat = 0;
  let step = 0;

  type Sends = { reverb?: number; echo?: number };

  /** Routes a voice's output to a bus, plus optional reverb and echo sends. */
  const route = (node: AudioNode, bus: AudioNode, sends: Sends = {}) => {
    node.connect(bus);
    if (sends.reverb) {
      const g = ctx!.createGain();
      g.gain.value = sends.reverb;
      node.connect(g);
      g.connect(reverbSend!);
    }
    if (sends.echo) {
      const g = ctx!.createGain();
      g.gain.value = sends.echo;
      node.connect(g);
      g.connect(echoSend!);
    }
  };

  /** A supersaw note into a lowpass; returns the amp gain so the caller shapes the envelope. */
  const supersaw = (
    t: number,
    frequency: number,
    duration: number,
    cutoff: number,
    voices = SUPERSAW,
    glideFrom?: number
  ) => {
    const filter = ctx!.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(cutoff, t);
    filter.Q.value = 0.8;
    const amp = ctx!.createGain();
    amp.gain.setValueAtTime(0.0001, t);
    filter.connect(amp);
    voices.forEach(({ cents, pan }) => {
      const osc = ctx!.createOscillator();
      osc.type = "sawtooth";
      if (glideFrom) {
        // Portamento: start on the previous note and slide up or down into this one.
        osc.frequency.setValueAtTime(glideFrom, t);
        osc.frequency.exponentialRampToValueAtTime(frequency, t + 0.06);
      } else {
        osc.frequency.value = frequency;
      }
      osc.detune.value = cents;
      const panner = ctx!.createStereoPanner();
      panner.pan.value = pan;
      osc.connect(panner);
      panner.connect(filter);
      osc.start(t);
      osc.stop(t + duration + 0.05);
    });
    return { amp, filter };
  };

  const noiseHit = (
    t: number,
    type: BiquadFilterType,
    frequency: number,
    peak: number,
    decay: number,
    sends?: Sends
  ) => {
    const source = ctx!.createBufferSource();
    source.buffer = noise;
    const filter = ctx!.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = frequency;
    const amp = ctx!.createGain();
    amp.gain.setValueAtTime(peak, t);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + decay);
    source.connect(filter);
    filter.connect(amp);
    route(amp, drums!, sends);
    source.start(t);
    source.stop(t + decay + 0.02);
  };

  const kick = (t: number) => {
    const osc = ctx!.createOscillator();
    osc.frequency.setValueAtTime(190, t);
    osc.frequency.exponentialRampToValueAtTime(48, t + 0.07);
    const drive = ctx!.createWaveShaper();
    drive.curve = DRIVE_CURVE;
    const amp = ctx!.createGain();
    amp.gain.setValueAtTime(1, t);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
    osc.connect(drive);
    drive.connect(amp);
    amp.connect(drums!);
    osc.start(t);
    osc.stop(t + 0.46);
    // The click on top, so the kick cuts through on laptop speakers.
    noiseHit(t, "highpass", 3000, 0.35, 0.012);

    // Sidechain: duck the melodic bus and let it swell back before the next beat.
    music!.gain.setValueAtTime(0.2, t);
    music!.gain.linearRampToValueAtTime(1, t + 0.22);
  };

  const bass = (t: number, frequency: number) => {
    const { amp, filter } = supersaw(t, frequency, SIXTEENTH, 900, [
      { cents: -6, pan: 0 },
      { cents: 6, pan: 0 }
    ]);
    filter.frequency.exponentialRampToValueAtTime(220, t + SIXTEENTH * 0.9);
    amp.gain.linearRampToValueAtTime(0.32, t + 0.004);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + SIXTEENTH * 0.95);
    route(amp, music!);
  };

  const pad = (t: number, chord: number[], cutoff: number) => {
    chord.forEach((f) => {
      const { amp } = supersaw(t, f, BAR, cutoff);
      amp.gain.linearRampToValueAtTime(0.045, t + 0.35);
      amp.gain.setValueAtTime(0.045, t + BAR - 0.2);
      amp.gain.linearRampToValueAtTime(0.0001, t + BAR);
      route(amp, music!, { reverb: 0.6 });
    });
  };

  /** One sustained supersaw chord per bar, chopped into sixteenths by the gate pattern. */
  const gatedChords = (t: number, chord: number[]) => {
    chord.forEach((f) => {
      const { amp } = supersaw(t, f * 2, BAR, 3800, SUPERSAW.slice(1, 6));
      GATE.forEach((open, i) => {
        if (!open) return;
        const ts = t + i * SIXTEENTH;
        amp.gain.setValueAtTime(0.0001, ts);
        amp.gain.linearRampToValueAtTime(0.05, ts + 0.005);
        amp.gain.linearRampToValueAtTime(0.0001, ts + SIXTEENTH * 0.8);
      });
      route(amp, music!, { reverb: 0.35, echo: 0.2 });
    });
  };

  const pluck = (t: number, frequency: number, level: number) => {
    const { amp, filter } = supersaw(
      t,
      frequency,
      0.3,
      5000,
      SUPERSAW.slice(2, 5)
    );
    filter.frequency.exponentialRampToValueAtTime(700, t + 0.2);
    amp.gain.linearRampToValueAtTime(level, t + 0.003);
    amp.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
    route(amp, music!, { reverb: 0.4, echo: 0.45 });
  };

  // The last lead note, so a note that starts exactly where the previous one ended can slide.
  let lastLead: { frequency: number; end: number } | null = null;

  const lead = (
    t: number,
    frequency: number,
    length: number,
    style: "drop" | "breakdown"
  ) => {
    const duration = SIXTEENTH * length;
    const breakdown = style === "breakdown";
    // Slides only in the breakdown, between its long notes. In the drop every note butts up
    // against the next, and sliding all of them smeared the hook.
    const legato =
      breakdown &&
      lastLead &&
      lastLead.frequency !== frequency &&
      Math.abs(lastLead.end - t) < 0.001;
    const glideFrom = legato ? lastLead!.frequency : undefined;
    lastLead = { frequency, end: t + duration };

    // Sixteenths a touch softer than held notes, so the fast figures sit behind the long ones
    // the way a played part would.
    const velocity = length === 1 ? 0.7 : length < 4 ? 0.9 : 1;

    // Doubled an octave up: the stacked supersaw lead of every anthem.
    [frequency, frequency * 2].forEach((f, i) => {
      const { amp } = supersaw(
        t,
        f,
        duration,
        breakdown ? 4200 : 6000,
        SUPERSAW,
        glideFrom && glideFrom * (i + 1)
      );
      const peak = (i === 0 ? 0.075 : 0.035) * velocity;
      // Breakdown notes swell in slowly; drop notes hit.
      amp.gain.linearRampToValueAtTime(peak, t + (breakdown ? 0.12 : 0.015));
      amp.gain.setValueAtTime(peak, t + duration * 0.85);
      amp.gain.exponentialRampToValueAtTime(
        0.0001,
        t + duration + (breakdown ? 0.4 : 0.12)
      );
      route(amp, music!, {
        reverb: breakdown ? 0.9 : 0.6,
        // Light echo in the drop: a dotted-eighth echo on a dotted-eighth rhythm doubles
        // every note.
        echo: breakdown ? 0.2 : 0.12
      });
    });
  };

  /** A bar of white noise sweeping up into the drop. */
  const riser = (t: number) => {
    const source = ctx!.createBufferSource();
    source.buffer = noise;
    source.loop = true;
    const filter = ctx!.createBiquadFilter();
    filter.type = "bandpass";
    filter.Q.value = 2;
    filter.frequency.setValueAtTime(300, t);
    filter.frequency.exponentialRampToValueAtTime(9000, t + BAR);
    const amp = ctx!.createGain();
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.exponentialRampToValueAtTime(0.25, t + BAR);
    source.connect(filter);
    filter.connect(amp);
    route(amp, drums!, { reverb: 0.5 });
    source.start(t);
    source.stop(t + BAR);
  };

  const play = (s: number, t: number) => {
    const inBar = s % 16;
    const bar = Math.floor(s / 16);
    const chordIndex = bar % CHORDS.length;
    const chord = CHORDS[chordIndex];
    const section = sectionAt(bar);
    // Bar within the 16-bar cycle: build 0-3, breakdown 4-7, drop 8-15.
    const cycleBar = (bar - 4) % CYCLE_BARS;
    const drumsOn = section !== "breakdown";

    if (inBar === 0) {
      pad(t, chord, PAD_CUTOFF[section]);
      if (section === "build" || section === "drop") gatedChords(t, chord);
      if (cycleBar === 7) riser(t);
      // A crash on the drop, and a smaller one where the hook's answer starts.
      if (cycleBar === 8) {
        noiseHit(t, "highpass", 4000, 0.3, 1.6, { reverb: 0.6 });
      }
      if (cycleBar === 12) {
        noiseHit(t, "highpass", 5000, 0.18, 1.1, { reverb: 0.5 });
      }
    }

    if (drumsOn) {
      if (inBar % 4 === 0) kick(t);
      if (section !== "intro" && (inBar === 4 || inBar === 12)) {
        noiseHit(t, "bandpass", 1600, 0.5, 0.16, { reverb: 0.4 });
      }
      // Closed hats on every sixteenth with accents, open hat on the off-beat.
      const accent = inBar % 4 === 2 ? 0.16 : inBar % 2 === 1 ? 0.07 : 0.04;
      noiseHit(t, "highpass", 8000, accent, 0.035);
      if (inBar % 4 === 2) noiseHit(t, "highpass", 7000, 0.12, 0.14);
      if (BASS_STEPS.has(inBar)) bass(t, ROOTS[chordIndex]);
    }

    if (section === "build" || section === "drop") {
      const tones = [...chord, ...chord.map((f) => f * 2)];
      // The arp carries the build, then steps back to half level under the hook.
      pluck(
        t,
        tones[ARP_PATTERN[inBar]] * 2,
        section === "drop" ? 0.055 : 0.11
      );
    }

    // The breakdown and drop bars line up with the chords: both start on an Am bar.
    const melodyBar =
      section === "breakdown"
        ? BREAKDOWN_THEME[cycleBar - 4]
        : section === "drop"
          ? DROP_HOOK[cycleBar - 8]
          : null;
    melodyBar
      ?.filter(([at]) => at === inBar)
      .forEach(([, f, length]) =>
        lead(t, f, length, section === "breakdown" ? "breakdown" : "drop")
      );
  };

  const schedule = () => {
    if (!ctx) return;
    while (nextTime < ctx.currentTime + LOOKAHEAD_S) {
      play(step, nextTime);
      nextTime += SIXTEENTH;
      step += 1;
    }
  };

  /** A generated stereo impulse response: decaying noise, about three seconds of hall. */
  const buildReverb = () => {
    const length = Math.floor(ctx!.sampleRate * 3);
    const impulse = ctx!.createBuffer(2, length, ctx!.sampleRate);
    for (let channel = 0; channel < 2; channel++) {
      const data = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 3);
      }
    }
    const convolver = ctx!.createConvolver();
    convolver.buffer = impulse;
    return convolver;
  };

  /** Dotted-eighth echo bouncing left and right. */
  const buildPingPong = (input: AudioNode, output: AudioNode) => {
    const left = ctx!.createDelay(1);
    const right = ctx!.createDelay(1);
    left.delayTime.value = SIXTEENTH * 3;
    right.delayTime.value = SIXTEENTH * 3;
    const feedback = ctx!.createGain();
    feedback.gain.value = 0.42;
    const panLeft = ctx!.createStereoPanner();
    panLeft.pan.value = -0.7;
    const panRight = ctx!.createStereoPanner();
    panRight.pan.value = 0.7;
    input.connect(left);
    left.connect(panLeft);
    panLeft.connect(output);
    left.connect(right);
    right.connect(panRight);
    panRight.connect(output);
    right.connect(feedback);
    feedback.connect(left);
  };

  return {
    start: () => {
      if (ctx) return;
      ctx = new AudioContext();
      // Some browsers create the context suspended even inside a gesture; resume is a no-op
      // when it is already running.
      void ctx.resume();

      // Kept well below full scale: this plays in an office.
      const master = ctx.createGain();
      master.gain.value = 0.3;
      const glue = ctx.createDynamicsCompressor();
      glue.threshold.value = -16;
      glue.ratio.value = 4;
      master.connect(glue);
      glue.connect(ctx.destination);

      drums = ctx.createGain();
      drums.connect(master);
      // Everything melodic, including the effect returns, sits on the sidechained bus so the
      // reverb tail pumps with the kick too.
      music = ctx.createGain();
      music.connect(master);

      reverbSend = ctx.createGain();
      const reverb = buildReverb();
      const reverbReturn = ctx.createGain();
      reverbReturn.gain.value = 0.6;
      reverbSend.connect(reverb);
      reverb.connect(reverbReturn);
      reverbReturn.connect(music);

      echoSend = ctx.createGain();
      buildPingPong(echoSend, music);

      noise = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
      const data = noise.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;

      nextTime = ctx.currentTime + 0.05;
      firstDownbeat = nextTime;
      step = 0;
      timer = setInterval(schedule, TICK_MS);
    },
    downbeatMs: () => {
      if (!ctx || ctx.state !== "running") return null;
      // Map the audio clock onto performance.now(), plus the time the sound takes to reach
      // the speakers, so the glow lands on the kick you hear rather than the one scheduled.
      const latency = (ctx.baseLatency ?? 0) + (ctx.outputLatency ?? 0);
      return (
        performance.now() + (firstDownbeat - ctx.currentTime + latency) * 1000
      );
    },
    stop: () => {
      if (timer) clearInterval(timer);
      timer = null;
      void ctx?.close();
      ctx = null;
      drums = null;
      music = null;
      reverbSend = null;
      echoSend = null;
      noise = null;
    }
  };
};
